import * as React from "react";
import {
  changePassword as apiChangePassword,
  claim as apiClaim,
  getAuthState,
  login as apiLogin,
  logout as apiLogout,
  type AuthState,
} from "@/api/admin";
import { clearToken, getToken, setSessionEndedHandler, setToken } from "@/api/client";

/**
 * Who is signed in, and what the sign-in screen should offer them.
 *
 * TWO DOORS, AND THIS IS THE INNER ONE. Cloudflare Access is in front of the
 * whole hostname, so by the time this code runs the person has already proved
 * who they are to an identity provider. What is left is the password, and the
 * shape of that question depends on the account:
 *
 *   - no admin row at all, and this is the bootstrap address  -> set up the shop
 *   - a row with no password (invited, or password revoked)   -> choose one
 *   - a row with a password                                   -> sign in
 *   - suspended                                               -> nothing to do
 *
 * `/auth/state` answers all four in one call, which is why the app asks it on
 * boot rather than guessing from a 401.
 */
interface AuthValue {
  /** Null until the first `/auth/state` answers. */
  state: AuthState | null;
  /** True once a bearer is in hand. */
  signedIn: boolean;
  /** Set while the boot call is in flight, so the app can hold its layout. */
  loading: boolean;
  /** The boot call failed -- Access is misconfigured, or the worker is down. */
  error: string | null;
  signIn(password: string): Promise<void>;
  claimAccount(password: string): Promise<void>;
  signOut(): Promise<void>;
  changePassword(current: string, next: string): Promise<void>;
  refresh(): Promise<void>;
}

const Ctx = React.createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<AuthState | null>(null);
  const [signedIn, setSignedIn] = React.useState(() => getToken() !== null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const refresh = React.useCallback(async () => {
    try {
      setState(await getAuthState());
      setError(null);
    } catch (err) {
      // A failure HERE is not "wrong password" -- nothing has been submitted.
      // It is Access not asserting an identity, or the worker being
      // unreachable, and the screen has to say so rather than show a password
      // box that cannot work.
      setError(err instanceof Error ? err.message : "Could not reach the API");
    }
  }, []);

  React.useEffect(() => {
    // The client calls this when the worker says the session is gone. Held here
    // rather than in the client so the transition is React state, not a
    // `location.assign` racing the router's own move.
    setSessionEndedHandler(() => {
      setSignedIn(false);
      void refresh();
    });
    void refresh().finally(() => setLoading(false));
  }, [refresh]);

  const signIn = React.useCallback(async (password: string) => {
    const { token } = await apiLogin(password);
    setToken(token);
    setSignedIn(true);
  }, []);

  const claimAccount = React.useCallback(
    async (password: string) => {
      const { token } = await apiClaim(password);
      setToken(token);
      setSignedIn(true);
      // The account has changed shape -- it now has a password -- so the state
      // the screen was drawn from is stale.
      await refresh();
    },
    [refresh],
  );

  const signOut = React.useCallback(async () => {
    // The token is dropped whatever the worker says. A logout that leaves the
    // bearer in the tab because the network hiccuped is not a logout, and the
    // endpoint is idempotent by design.
    try {
      await apiLogout();
    } catch {
      /* Already gone, or unreachable. Either way we are done with it. */
    }
    clearToken();
    setSignedIn(false);
    await refresh();
  }, [refresh]);

  const changePassword = React.useCallback(async (current: string, next: string) => {
    // The worker closes every other session and hands back a replacement, so
    // the operator who just changed their password is not thrown out for it.
    const { token } = await apiChangePassword(current, next);
    setToken(token);
    setSignedIn(true);
  }, []);

  const value = React.useMemo<AuthValue>(
    () => ({ state, signedIn, loading, error, signIn, claimAccount, signOut, changePassword, refresh }),
    [state, signedIn, loading, error, signIn, claimAccount, signOut, changePassword, refresh],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = React.useContext(Ctx);
  if (!ctx) throw new Error("useAuth used outside AuthProvider");
  return ctx;
}
